import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Input as ChakraInput,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { RiAddLine, RiCheckFill, RiCloseCircleFill, RiDeleteBin6Line, RiEditLine } from 'react-icons/ri'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { usePermission } from '../../hooks/usePermission'
import { DirectPermission, SystemPermission, User } from '../../Interfaces/users'
import { api } from '../../services/api'

const MANAGE_PERMISSIONS = ['permissions.manage', 'roles.manage']

export type PermissionStatus = 'ALLOW' | 'DENY' | 'INHERIT'

const STATUS_LABELS: Record<PermissionStatus, string> = {
  ALLOW: 'Permitir',
  DENY: 'Bloquear',
  INHERIT: 'Herdar',
}

const STATUS_COLOR: Record<PermissionStatus, string> = {
  ALLOW: 'green',
  DENY: 'red',
  INHERIT: 'gray',
}

type PermissionFormValues = {
  name: string
  slug: string
  description?: string
  category?: string
  isMenu: boolean
  displayOrder?: number
  parentPermissionId?: number | ''
}

const DEFAULT_PERMISSION_VALUES: PermissionFormValues = {
  name: '',
  slug: '',
  description: '',
  category: 'Geral',
  isMenu: false,
  displayOrder: 0,
  parentPermissionId: '',
}

const getDirectStatus = (permissions: DirectPermission[] = [], slug: string): PermissionStatus => {
  const direct = permissions.find((permission) => permission.slug === slug)
  if (!direct) return 'INHERIT'

  const grant = direct.grantType || direct.grant_type || direct.pivot_grant_type
  if (grant === 'ALLOW') return 'ALLOW'
  if (grant === 'DENY') return 'DENY'
  return 'INHERIT'
}

const getEffectiveStatus = (user: User | undefined, slug: string): boolean => {
  if (!user?.permissionSlugs) return false
  return user.permissionSlugs.includes(slug)
}

function useAccessControlUsers(enabled: boolean) {
  return useQuery(
    ['access-control-users'],
    async () => {
      const { data } = await api.get('users', {
        params: {
          perPage: 100,
        },
      })

      return (data?.data || data || []) as User[]
    },
    {
      enabled,
    },
  )
}

function useAccessControlUserDetails(userId: number | null) {
  return useQuery(
    ['access-control-user', userId],
    async () => {
      if (!userId) return null
      const { data } = await api.get<User>('users/' + userId)
      return data
    },
    {
      enabled: !!userId,
    },
  )
}

function useSystemPermissions(enabled: boolean) {
  return useQuery(
    ['access-control-permissions'],
    async () => {
      const { data } = await api.get<SystemPermission[]>('permissions')
      return data
    },
    {
      enabled,
    },
  )
}

export default function AccessControlTab() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { hasAnyPermission } = usePermission()

  const canManageAccess = hasAnyPermission(MANAGE_PERMISSIONS)

  const {
    data: permissionsData,
    isLoading: loadingPermissions,
    isError: permissionsError,
  } = useSystemPermissions(canManageAccess)
  const { data: users, isLoading: loadingUsers, isError: usersError } = useAccessControlUsers(canManageAccess)

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const { data: selectedUser, isFetching: loadingUserDetails } = useAccessControlUserDetails(selectedUserId)

  const permissionSlugs = useMemo(
    () => (permissionsData ? permissionsData.map((permission) => permission.slug) : []),
    [permissionsData],
  )

  const groupedPermissions = useMemo(() => {
    if (!permissionsData) return [] as { category: string; items: SystemPermission[] }[]

    const groups = permissionsData.reduce<Record<string, SystemPermission[]>>((acc, permission) => {
      const category = permission.category || 'Outros'
      if (!acc[category]) acc[category] = []
      acc[category].push(permission)
      return acc
    }, {})

    return Object.entries(groups)
      .map(([category, items]) => ({
        category,
        items: items
          .slice()
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.category.localeCompare(b.category))
  }, [permissionsData])

  const [permissionState, setPermissionState] = useState<Record<string, PermissionStatus>>({})
  const [initialState, setInitialState] = useState<Record<string, PermissionStatus>>({})

  useEffect(() => {
    if (!selectedUser || !permissionSlugs.length) {
      setPermissionState({})
      setInitialState({})
      return
    }

    const state = permissionSlugs.reduce<Record<string, PermissionStatus>>((acc, slug) => {
      acc[slug] = getDirectStatus(selectedUser.directPermissions, slug)
      return acc
    }, {})

    setPermissionState(state)
    setInitialState({ ...state })
  }, [selectedUser, permissionSlugs])

  const isDirty = useMemo(() => {
    const combined = Object.keys(permissionState)
    Object.keys(initialState).forEach((slug) => {
      if (!combined.includes(slug)) {
        combined.push(slug)
      }
    })

    return combined.some((slug) => permissionState[slug] !== initialState[slug])
  }, [permissionState, initialState])

  const updatePermissionState = (slug: string, status: PermissionStatus) => {
    setPermissionState((prev) => ({ ...prev, [slug]: status }))
  }

  const saveMutation = useMutation(
    async () => {
      if (!selectedUserId) return

      const payload = Object.entries(permissionState)
        .filter(([, status]) => status !== 'INHERIT')
        .map(([slug, status]) => ({
          slug,
          grantType: status,
        }))

      await api.put('users/' + selectedUserId + '/permissions', {
        permissions: payload,
      })
    },
    {
      onSuccess: async () => {
        toast({ description: 'Permissões atualizadas com sucesso', status: 'success' })
        if (selectedUserId) {
          await queryClient.invalidateQueries(['access-control-user', selectedUserId])
        }
        setInitialState({ ...permissionState })
      },
      onError: (error: any) => {
        const description = error?.response?.data?.error || 'Não foi possível atualizar as permissões.'
        toast({ description, status: 'error' })
      },
    },
  )

  const permissionModal = useDisclosure()
  const deleteDialog = useDisclosure()
  const cancelDeleteRef = useRef<HTMLButtonElement>(null)

  const [editingPermission, setEditingPermission] = useState<SystemPermission | null>(null)
  const [permissionToDelete, setPermissionToDelete] = useState<SystemPermission | null>(null)

  const {
    register: registerPermissionForm,
    handleSubmit: handleSubmitPermissionForm,
    reset: resetPermissionForm,
    formState: { isSubmitting: permissionFormSubmitting },
  } = useForm<PermissionFormValues>({
    defaultValues: DEFAULT_PERMISSION_VALUES,
  })

  const openCreatePermission = () => {
    setEditingPermission(null)
    resetPermissionForm(DEFAULT_PERMISSION_VALUES)
    permissionModal.onOpen()
  }

  const openEditPermission = (permission: SystemPermission) => {
    setEditingPermission(permission)
    resetPermissionForm({
      name: permission.name,
      slug: permission.slug,
      description: permission.description || '',
      category: permission.category || 'Geral',
      isMenu: Boolean(permission.isMenu),
      displayOrder: permission.displayOrder ?? 0,
      parentPermissionId: permission.parentPermissionId ?? '',
    })
    permissionModal.onOpen()
  }

  const closePermissionModal = () => {
    permissionModal.onClose()
    setEditingPermission(null)
  }

  const createPermissionMutation = useMutation(
    async (payload: PermissionFormValues) => {
      await api.post('permissions', {
        name: payload.name,
        slug: payload.slug,
        description: payload.description,
        category: payload.category || 'Geral',
        isMenu: payload.isMenu,
        parentPermissionId: payload.parentPermissionId ?? null,
        displayOrder: payload.displayOrder ?? 0,
      })
    },
    {
      onSuccess: async () => {
        toast({ description: 'Permissão criada com sucesso.', status: 'success' })
        await queryClient.invalidateQueries(['access-control-permissions'])
        if (selectedUserId) {
          await queryClient.invalidateQueries(['access-control-user', selectedUserId])
        }
        closePermissionModal()
      },
      onError: (error: any) => {
        const description = error?.response?.data?.error || 'Não foi possível criar a permissão.'
        toast({ description, status: 'error' })
      },
    },
  )

  const updatePermissionMutation = useMutation(
    async ({ id, payload }: { id: number; payload: PermissionFormValues }) => {
      await api.put('permissions/' + id, {
        name: payload.name,
        slug: payload.slug,
        description: payload.description,
        category: payload.category || 'Geral',
        isMenu: payload.isMenu,
        parentPermissionId: payload.parentPermissionId ?? null,
        displayOrder: payload.displayOrder ?? 0,
      })
    },
    {
      onSuccess: async () => {
        toast({ description: 'Permissão atualizada com sucesso.', status: 'success' })
        await queryClient.invalidateQueries(['access-control-permissions'])
        if (selectedUserId) {
          await queryClient.invalidateQueries(['access-control-user', selectedUserId])
        }
        closePermissionModal()
      },
      onError: (error: any) => {
        const description = error?.response?.data?.error || 'Não foi possível atualizar a permissão.'
        toast({ description, status: 'error' })
      },
    },
  )

  const deletePermissionMutation = useMutation(
    async (id: number) => {
      await api.delete('permissions/' + id)
    },
    {
      onSuccess: async () => {
        toast({ description: 'Permissão removida com sucesso.', status: 'success' })
        await queryClient.invalidateQueries(['access-control-permissions'])
        if (selectedUserId) {
          await queryClient.invalidateQueries(['access-control-user', selectedUserId])
        }
        deleteDialog.onClose()
        setPermissionToDelete(null)
      },
      onError: (error: any) => {
        const description = error?.response?.data?.error || 'Não foi possível remover a permissão.'
        toast({ description, status: 'error' })
      },
    },
  )

  const onSubmitPermissionForm = (values: PermissionFormValues) => {
    const payload: PermissionFormValues = {
      ...values,
      category: values.category?.trim() || 'Geral',
      parentPermissionId: values.parentPermissionId === '' ? undefined : Number(values.parentPermissionId),
    }

    if (editingPermission) {
      return updatePermissionMutation.mutateAsync({ id: editingPermission.id, payload })
    }

    return createPermissionMutation.mutateAsync(payload)
  }

  const openDeletePermission = (permission: SystemPermission) => {
    setPermissionToDelete(permission)
    deleteDialog.onOpen()
  }

  const confirmDeletePermission = () => {
    if (permissionToDelete) {
      deletePermissionMutation.mutate(permissionToDelete.id)
    }
  }

  if (!canManageAccess) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <Box>
          <AlertTitle>Permissão insuficiente</AlertTitle>
          <AlertDescription>
            Você precisa da permissão <Badge colorScheme="purple">permissions.manage</Badge> ou{' '}
            <Badge colorScheme="purple">roles.manage</Badge> para administrar o controle de acesso.
          </AlertDescription>
        </Box>
      </Alert>
    )
  }

  if (usersError || permissionsError) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertTitle>Erro ao carregar dados</AlertTitle>
        <AlertDescription>Tente novamente mais tarde.</AlertDescription>
      </Alert>
    )
  }

  return (
    <Stack spacing={6}>
      <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={6} bg="gray.800">
        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={4}>
          <Heading size="md">Cadastro de telas e menus</Heading>
          <Button
            leftIcon={<RiAddLine />}
            bg="pink.500"
            color="white"
            _hover={{ bg: 'pink.400' }}
            _active={{ bg: 'pink.600' }}
            onClick={openCreatePermission}
            isDisabled={loadingPermissions}>
            Nova permissão
          </Button>
        </Flex>

        {loadingPermissions ? (
          <HStack spacing={2}>
            <Spinner size="sm" />
            <Text>Carregando permissões cadastradas...</Text>
          </HStack>
        ) : !permissionsData?.length ? (
          <Text color="gray.300">Nenhuma permissão cadastrada até o momento.</Text>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Nome</Th>
                <Th>Slug</Th>
                <Th>Categoria</Th>
                <Th>Tipo</Th>
                <Th isNumeric>Ordem</Th>
                <Th textAlign="right">Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {groupedPermissions.map(({ category, items }) => (
                <Fragment key={category}>
                  <Tr bg="gray.900">
                    <Td colSpan={6} fontWeight="bold" color="pink.300">
                      {category}
                    </Td>
                  </Tr>
                  {items.map((permission) => (
                    <Tr key={permission.id}>
                      <Td>{permission.name}</Td>
                      <Td>
                        <Text fontFamily="mono" fontSize="sm">
                          {permission.slug}
                        </Text>
                      </Td>
                      <Td>{permission.category || 'Geral'}</Td>
                      <Td>
                        <Badge colorScheme={permission.isMenu ? 'purple' : 'blue'}>
                          {permission.isMenu ? 'Menu' : 'Tela'}
                        </Badge>
                      </Td>
                      <Td isNumeric>{permission.displayOrder ?? 0}</Td>
                      <Td textAlign="right">
                        <HStack spacing={2} justify="flex-end">
                          <Tooltip label="Editar">
                            <IconButton
                              aria-label="Editar permissão"
                              size="sm"
                              icon={<RiEditLine />}
                              onClick={() => openEditPermission(permission)}
                              colorScheme="blue"
                              variant="ghost"
                            />
                          </Tooltip>
                          <Tooltip label="Remover">
                            <IconButton
                              aria-label="Remover permissão"
                              size="sm"
                              icon={<RiDeleteBin6Line />}
                              onClick={() => openDeletePermission(permission)}
                              colorScheme="red"
                              variant="ghost"
                              isLoading={deletePermissionMutation.isLoading && permissionToDelete?.id === permission.id}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Fragment>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={6} bg="gray.800">
        <Text color="gray.300" mb={2}>
          Escolha um usuário para visualizar e ajustar o acesso às telas do sistema.
        </Text>
        <FormControl maxW="320px" mb={6}>
          <FormLabel fontSize="sm">Usuário</FormLabel>
          <Select
            placeholder={loadingUsers ? 'Carregando usuários...' : 'Selecione um usuário'}
            value={selectedUserId ?? ''}
            onChange={(event) => {
              const value = Number(event.target.value)
              setSelectedUserId(Number.isNaN(value) ? null : value)
            }}
            disabled={loadingUsers || !users?.length}
            color="white"
            bg="gray.700"
            borderColor="gray.600"
            _hover={{
              borderColor: 'gray.500',
            }}
            _focus={{
              color: 'black',
              bg: 'white',
              borderColor: 'pink.500',
            }}
            _active={{
              color: 'white',
              bg: 'gray.700',
            }}
            sx={{
              '& option': {
                color: 'black',
                bg: 'white',
              },
            }}>
            {users?.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.username})
              </option>
            ))}
          </Select>
        </FormControl>

        {selectedUserId ? (
          <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={6} bg="gray.900" overflowX="auto">
            {loadingUserDetails || loadingPermissions ? (
              <HStack spacing={2}>
                <Spinner size="sm" />
                <Text>Carregando permissões...</Text>
              </HStack>
            ) : !selectedUser ? (
              <Text>Não foi possível carregar os dados deste usuário.</Text>
            ) : (
              <VStack align="stretch" spacing={6}>
                <Box>
                  <Text fontSize="lg" fontWeight="bold">
                    {selectedUser.name}
                  </Text>
                  <HStack spacing={3} mt={2} flexWrap="wrap">
                    <Badge colorScheme="purple">{selectedUser.username}</Badge>
                    <Badge colorScheme="blue">{selectedUser.email}</Badge>
                    <Badge colorScheme="pink">Perfil: {selectedUser.access}</Badge>
                    {selectedUser.roles?.map((role) => (
                      <Badge key={role.id} colorScheme="green">
                        Cargo: {role.name}
                      </Badge>
                    ))}
                  </HStack>
                </Box>

                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Tela / funcionalidade</Th>
                      <Th w="200px">Acesso efetivo</Th>
                      <Th w="280px">Configuração</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {groupedPermissions.map(({ category, items }) => (
                      <Fragment key={category}>
                        <Tr bg="gray.800">
                          <Td colSpan={3} fontWeight="bold" color="pink.300">
                            {category}
                          </Td>
                        </Tr>
                        {items.map((item) => {
                          const value = permissionState[item.slug] ?? 'INHERIT'
                          const effective = getEffectiveStatus(selectedUser, item.slug)

                          return (
                            <Tr key={item.slug}>
                              <Td>
                                <VStack align="start" spacing={1}>
                                  <Text fontWeight="medium">
                                    {item.name}
                                    {item.isMenu ? ' (Menu)' : ''}
                                  </Text>
                                  <Text fontSize="sm" color="gray.400">
                                    {item.description}
                                  </Text>
                                </VStack>
                              </Td>
                              <Td>
                                <HStack spacing={2}>
                                  <Icon
                                    as={effective ? RiCheckFill : RiCloseCircleFill}
                                    color={effective ? 'green.300' : 'red.300'}
                                  />
                                  <Text>{effective ? 'Permitido' : 'Bloqueado'}</Text>
                                  {value === 'INHERIT' && <Badge colorScheme="yellow">herdado</Badge>}
                                </HStack>
                              </Td>
                              <Td>
                                <ButtonGroup size="sm" isAttached variant="outline">
                                  {(['ALLOW', 'DENY', 'INHERIT'] as PermissionStatus[]).map((status) => (
                                    <Button
                                      key={status}
                                      onClick={() => updatePermissionState(item.slug, status)}
                                      colorScheme={value === status ? STATUS_COLOR[status] : 'gray'}
                                      variant={value === status ? 'solid' : 'outline'}>
                                      {STATUS_LABELS[status]}
                                    </Button>
                                  ))}
                                </ButtonGroup>
                              </Td>
                            </Tr>
                          )
                        })}
                      </Fragment>
                    ))}
                  </Tbody>
                </Table>

                <HStack justify="flex-end" spacing={4}>
                  <Button
                    variant="ghost"
                    onClick={() => setPermissionState({ ...initialState })}
                    disabled={!isDirty || saveMutation.isLoading}>
                    Reverter alterações
                  </Button>
                  <Button
                    colorScheme="pink"
                    onClick={() => saveMutation.mutate()}
                    isLoading={saveMutation.isLoading}
                    disabled={!isDirty}>
                    Salvar permissões
                  </Button>
                </HStack>
              </VStack>
            )}
          </Box>
        ) : (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box color="gray.900">
              <AlertTitle color="gray.900">Selecione um usuário</AlertTitle>
              <AlertDescription color="gray.900">
                Escolha um usuário acima para revisar ou alterar o acesso às telas do sistema.
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </Box>

      <Modal isOpen={permissionModal.isOpen} onClose={closePermissionModal} size="lg">
        <ModalOverlay />
        <ModalContent
          as="form"
          onSubmit={handleSubmitPermissionForm(onSubmitPermissionForm)}
          bg="#181b23"
          color="white">
          <ModalHeader>{editingPermission ? 'Editar permissão' : 'Nova permissão'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nome</FormLabel>
                <ChakraInput placeholder="Nome da tela/menu" {...registerPermissionForm('name')} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Slug</FormLabel>
                <ChakraInput placeholder="Ex: users.manage" {...registerPermissionForm('slug')} autoCapitalize="none" />
                <FormHelperText>Identificador único usado para controlar o acesso.</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Categoria / Menu</FormLabel>
                <ChakraInput placeholder="Ex: Cadastros" {...registerPermissionForm('category')} />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <Switch id="isMenu" mr={3} {...registerPermissionForm('isMenu')}>
                  Menu
                </Switch>
                <FormLabel htmlFor="isMenu" mb="0">
                  Esta permissão representa um item de menu
                </FormLabel>
              </FormControl>

              <FormControl>
                <FormLabel>Ordem de exibição</FormLabel>
                <ChakraInput
                  type="number"
                  placeholder="0"
                  {...registerPermissionForm('displayOrder', { valueAsNumber: true })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Permissão pai (opcional)</FormLabel>
                <Select placeholder="Nenhum" {...registerPermissionForm('parentPermissionId')}>
                  <option value="">Nenhum</option>
                  {permissionsData
                    ?.filter((permission) => !editingPermission || permission.id !== editingPermission.id)
                    .map((permission) => (
                      <option key={permission.id} value={permission.id}>
                        {permission.name}
                      </option>
                    ))}
                </Select>
                <FormHelperText>Use para relacionar permissões hierarquicamente.</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Descrição</FormLabel>
                <Textarea
                  placeholder="Descreva a finalidade desta permissão"
                  rows={3}
                  {...registerPermissionForm('description')}
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closePermissionModal} disabled={permissionFormSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" colorScheme="pink" isLoading={permissionFormSubmitting}>
              {editingPermission ? 'Salvar alterações' : 'Criar permissão'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={deleteDialog.isOpen} leastDestructiveRef={cancelDeleteRef} onClose={deleteDialog.onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remover permissão
            </AlertDialogHeader>

            <AlertDialogBody>
              Tem certeza que deseja remover a permissão
              {permissionToDelete ? ` "${permissionToDelete.name}"` : ''}? Esta ação não poderá ser desfeita.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelDeleteRef}
                onClick={() => {
                  setPermissionToDelete(null)
                  deleteDialog.onClose()
                }}>
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDeletePermission}
                ml={3}
                isLoading={deletePermissionMutation.isLoading}>
                Remover
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Stack>
  )
}
